import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { useDispatch, useSelector } from "react-redux";
import { MdOutlineFileUpload } from "react-icons/md";

import BrowseUploadButton from "../../common/button/browseUploadButton";
import MauvelousPrimaryButton from "../../common/button/mauvelousPrimaryButton";
import CancelButton from "../../common/button/cancelButton";
import refundActions from "../../redux/refund/actions";

const TEMPLATE_HEADER = "receiptno,refundpaymentid,refundedamount";

/**
 * Bulk "mark as refunded" from a CSV, in two steps.
 *
 * The upload only reports what it would do; a second click applies it. Splitting them is what
 * lets the user see, before anything is written, that (say) 18 of 20 rows will go through and
 * exactly why the other two will not.
 */
export default function BulkRefundUploadDialog({ open, filters, onClose }) {
    const dispatch = useDispatch();
    const { bulkRefundPreview } = useSelector((state) => state.refundReducer);
    const { buttonLoader } = useSelector((state) => state.commonReducer);

    const [file, setFile] = useState(null);

    // A dry run describes one specific file. Leaving it on screen after the dialog is reopened
    // would invite the user to confirm a token for a file they are no longer looking at.
    useEffect(() => {
        if (open) {
            setFile(null);
            dispatch({ type: refundActions.CLEAR_BULK_REFUND_PREVIEW });
        }
    }, [open, dispatch]);

    const handleFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        setFile(selected);
        // Any earlier dry run belonged to the previous file.
        dispatch({ type: refundActions.CLEAR_BULK_REFUND_PREVIEW });
    };

    const handlePreview = () => {
        if (!file) return;
        dispatch({ type: refundActions.PREVIEW_BULK_REFUND, payload: { file } });
    };

    const handleConfirm = () => {
        dispatch({
            type: refundActions.CONFIRM_BULK_REFUND,
            payload: {
                token: bulkRefundPreview?.token,
                preview: bulkRefundPreview,
                filters,
                onSuccess: onClose
            }
        });
    };

    const handleDownloadTemplate = () => {
        const blob = new Blob([`${TEMPLATE_HEADER}\n`], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "manual-refund-template.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const errors = bulkRefundPreview?.errors || [];
    const validRows = bulkRefundPreview?.result || [];
    const canConfirm = !!bulkRefundPreview?.token && validRows.length > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontSize: "18px", fontWeight: 600 }}>
                Bulk Mark as Refunded
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Upload a CSV with the candidates you have already refunded on the payment
                    gateway. Required column: <b>receiptno</b>. Optional:{" "}
                    <b>refundpaymentid</b>, <b>refundedamount</b>.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    The exported refund list can be filled in and uploaded back as-is.{" "}
                    <Link component="button" type="button" onClick={handleDownloadTemplate}>
                        Download a blank template
                    </Link>
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                    <BrowseUploadButton
                        id="refund-csv-upload"
                        name="refundfile"
                        title="Choose CSV"
                        accept=".csv"
                        icon={<MdOutlineFileUpload size={18} />}
                        onChange={handleFileChange}
                    />
                    <Typography variant="body2">{file ? file.name : "No file chosen"}</Typography>
                    <MauvelousPrimaryButton
                        title={buttonLoader && !bulkRefundPreview ? "loader" : "Upload & Preview"}
                        handleClick={handlePreview}
                        disabled={!file || buttonLoader}
                    />
                </Box>

                {bulkRefundPreview && (
                    <>
                        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                            <Chip label={`${bulkRefundPreview.total} row(s) read`} />
                            <Chip
                                color="success"
                                label={`${bulkRefundPreview.willapply} will be marked refunded`}
                            />
                            {errors.length > 0 && (
                                <Chip color="error" label={`${errors.length} rejected`} />
                            )}
                        </Box>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            Nothing has been recorded yet. Review the rows below, then confirm.
                        </Alert>

                        {errors.length > 0 && (
                            <TableContainer
                                component={Paper}
                                sx={{ boxShadow: "none", border: "1px solid #e0e0e0", mb: 2, maxHeight: 220 }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Row</TableCell>
                                            <TableCell>Receipt No</TableCell>
                                            <TableCell>Reason it was rejected</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {errors.map((error, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{error.row}</TableCell>
                                                <TableCell>{error.receiptno || "-"}</TableCell>
                                                <TableCell sx={{ color: "error.main" }}>
                                                    {error.error}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {validRows.length > 0 && (
                            <TableContainer
                                component={Paper}
                                sx={{ boxShadow: "none", border: "1px solid #e0e0e0", maxHeight: 260 }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Row</TableCell>
                                            <TableCell>Candidate Name</TableCell>
                                            <TableCell>Receipt No</TableCell>
                                            <TableCell>Refund Payment ID</TableCell>
                                            <TableCell>Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {validRows.map((row, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{row.row}</TableCell>
                                                <TableCell>{row.candidatename}</TableCell>
                                                <TableCell>{row.receiptno}</TableCell>
                                                <TableCell>{row.refundpaymentid || "-"}</TableCell>
                                                <TableCell>{row.refundedamount}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {!canConfirm && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                No row can be applied. Correct the file and upload it again.
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <CancelButton title="Cancel" handleBack={onClose} />
                    <MauvelousPrimaryButton
                        title={buttonLoader && bulkRefundPreview ? "loader" : "Confirm & Mark Refunded"}
                        handleClick={handleConfirm}
                        disabled={!canConfirm || buttonLoader}
                    />
                </Box>
            </DialogActions>
        </Dialog>
    );
}
